// import type { MenuProps } from 'antd';
import {
  HomeOutlined,
  FileTextOutlined,
  ShopOutlined,
  ShoppingOutlined,
  InboxOutlined,
  DatabaseOutlined,
  TeamOutlined,
  DollarOutlined,
  SettingOutlined,
  AppstoreOutlined,
  PercentageOutlined,
  CalculatorOutlined,
  UserOutlined,
  SafetyOutlined,
  BuildOutlined,
  ImportOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  SwapOutlined,
  ToolOutlined,
  BellOutlined,
  DeleteOutlined,
  HistoryOutlined,
  AccountBookOutlined,
} from '@ant-design/icons';
import React from 'react';

export interface MenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  path?: string;
  roles?: string[];
  children?: MenuItem[];
}

// 运营角色组
const OPS_ROLES = ['ops', 'admin', 'salesperson', 'sales_manager', 'finance', 'purchaser', 'clerk'];
// 技术角色组
const TECH_ROLES = ['technician', 'tech_manager'];
// 工厂角色组
const FACTORY_ROLES = ['factory'];
// 门店端角色组
const STORE_CUSTOMER_ROLES = ['store_customer'];
// 财务角色组
const FINANCE_ROLES = ['finance', 'admin', 'ops'];
// 采购角色组
const PURCHASE_ROLES = ['purchaser', 'admin', 'ops', 'finance'];
// 系统管理角色组
const ADMIN_ROLES = ['admin', 'ops'];

export const menuItems: MenuItem[] = [
  {
    key: 'dashboard',
    label: '工作台',
    icon: React.createElement(HomeOutlined),
    path: '/',
    roles: [...OPS_ROLES, ...FACTORY_ROLES, ...STORE_CUSTOMER_ROLES],
  },
  {
    key: 'orders',
    label: '订单中心',
    icon: React.createElement(FileTextOutlined),
    roles: [...OPS_ROLES, ...TECH_ROLES, ...FACTORY_ROLES, ...STORE_CUSTOMER_ROLES],
    children: [
      { key: 'orders-create', label: '新建订单', path: '/orders/create', icon: React.createElement(ShopOutlined), roles: [...OPS_ROLES, ...STORE_CUSTOMER_ROLES] },
      { key: 'orders-all', label: '所有订单', path: '/orders', icon: React.createElement(FileTextOutlined), roles: [...OPS_ROLES, ...TECH_ROLES, ...FACTORY_ROLES, ...STORE_CUSTOMER_ROLES] },
      { key: 'finance-invoices', label: '发票管理', path: '/finance/invoices', icon: React.createElement(FileTextOutlined), roles: [...FINANCE_ROLES, ...STORE_CUSTOMER_ROLES, 'admin', 'ops', 'salesperson', 'sales_manager'] },
      { key: 'orders-quote', label: '价格计算器', path: '/finance/quote', icon: React.createElement(CalculatorOutlined), roles: [...FINANCE_ROLES, 'salesperson', 'sales_manager'] },
      { key: 'orders-sales-prices', label: '销售价格表', path: '/finance/sales-prices', icon: React.createElement(PercentageOutlined), roles: [...FINANCE_ROLES, 'salesperson', 'sales_manager'] },
      { key: 'orders-after-sales', label: '售后管理', path: '/orders/after-sales', icon: React.createElement(ToolOutlined), roles: [...OPS_ROLES, ...TECH_ROLES, ...STORE_CUSTOMER_ROLES] },
      { key: 'orders-hp-history', label: '历史订单', path: '/orders/hp-history', icon: React.createElement(HistoryOutlined), roles: ['admin', 'ops', 'technician', 'tech_manager', 'salesperson', 'sales_manager'] },
      { key: 'orders-my-purchase', label: '我的采购', path: '/orders/my-purchase', icon: React.createElement(ShoppingOutlined), roles: [...OPS_ROLES] },
    ],
  },
  {
    key: 'purchase',
    label: '采购管理',
    icon: React.createElement(ShoppingOutlined),
    roles: PURCHASE_ROLES,
    children: [
      { key: 'purchase-apply', label: '采购申请', path: '/purchase/apply', icon: React.createElement(ShoppingOutlined), roles: PURCHASE_ROLES },
      { key: 'purchase-inventory', label: '面料库存', path: '/purchase/inventory', icon: React.createElement(InboxOutlined), roles: PURCHASE_ROLES },
      { key: 'purchase-fabric-import', label: '样册导入', path: '/purchase/fabric-import', icon: React.createElement(ImportOutlined), roles: PURCHASE_ROLES },
      { key: 'purchase-sample-books', label: '样本册管理', path: '/purchase/sample-books', icon: React.createElement(AccountBookOutlined), roles: PURCHASE_ROLES },
      { key: 'purchase-order-inventory', label: '订单出入库', path: '/purchase/order-inventory', icon: React.createElement(SwapOutlined), roles: PURCHASE_ROLES },
    ],
  },
  {
    key: 'master',
    label: '基础数据管理',
    icon: React.createElement(DatabaseOutlined),
    roles: ADMIN_ROLES,
    children: [
      { key: 'master-price-system', label: '价格体系配置', path: '/master/price-system', icon: React.createElement(PercentageOutlined), roles: ADMIN_ROLES },
      { key: 'master-commission', label: '提成规则配置', path: '/master/commission-rules', icon: React.createElement(CalculatorOutlined), roles: ADMIN_ROLES },
      { key: 'master-penalty', label: '处罚规则', path: '/master/penalty-rules', icon: React.createElement(SafetyOutlined), roles: ADMIN_ROLES },
      { key: 'master-base-config', label: '基础订单配置', path: '/master/base-order-config', icon: React.createElement(SettingOutlined), roles: [...ADMIN_ROLES, 'sales_manager'] },
      { key: 'master-ease-rule', label: '加放规则维护', path: '/master/ease-rule', icon: React.createElement(BuildOutlined), roles: [...ADMIN_ROLES, ...TECH_ROLES] },
      { key: 'master-option-rules', label: '工艺互斥关联规则', path: '/master/option-rules', icon: React.createElement(SettingOutlined), roles: ADMIN_ROLES },
    ],
  },
  {
    key: 'supplier',
    label: '供应商管理',
    icon: React.createElement(TeamOutlined),
    // 分组权限放宽到 FINANCE_ROLES（新增 finance），使财务人员能看到本分组（为了显示下面的「加工商对账」）；
    // 其它子项（物料供应商/工厂供应商/工厂数据映射）仍各自限定 ADMIN_ROLES，财务不可见
    roles: FINANCE_ROLES,
    children: [
      { key: 'finance-factory-reconciliation', label: '加工商对账', path: '/finance/factory-reconciliations', icon: React.createElement(AccountBookOutlined), roles: FINANCE_ROLES },
      { key: 'supplier-material', label: '物料供应商', path: '/supplier/material', icon: React.createElement(TeamOutlined), roles: [...ADMIN_ROLES, 'purchaser'] },
      { key: 'supplier-factory', label: '工厂供应商', path: '/supplier/factory', icon: React.createElement(BuildOutlined), roles: ADMIN_ROLES },
      { key: 'supplier-factory-mapping', label: '工厂数据映射', path: '/supplier/factory-mapping', icon: React.createElement(BuildOutlined), roles: ADMIN_ROLES },
    ],
  },
  {
    key: 'ready-wear',
    label: '成衣商城',
    icon: React.createElement(AppstoreOutlined),
    path: '/ready-wear',
    roles: ADMIN_ROLES,
  },
  {
    key: 'stores',
    label: '门店管理',
    icon: React.createElement(ShopOutlined),
    roles: [...OPS_ROLES, ...TECH_ROLES, ...STORE_CUSTOMER_ROLES],
    children: [
      { key: 'stores-management', label: '门店管理', path: '/store-management', icon: React.createElement(ShopOutlined), roles: OPS_ROLES },
      { key: 'stores-accounts', label: '门店账号（旧）', path: '/stores/accounts', icon: React.createElement(UserOutlined), roles: OPS_ROLES },
      { key: 'stores-list', label: '门店列表（旧）', path: '/stores', icon: React.createElement(ShopOutlined), roles: [...OPS_ROLES, ...TECH_ROLES, ...STORE_CUSTOMER_ROLES] },
      { key: 'stores-registration-audit', label: '客户注册审核', path: '/stores/registration-audit', icon: React.createElement(CheckCircleOutlined), roles: ['admin', 'ops', 'sales_manager', 'salesperson'] },

    ],
  },
  {
    key: 'teams',
    label: '团队管理',
    icon: React.createElement(TeamOutlined),
    roles: [...OPS_ROLES, ...ADMIN_ROLES],
    children: [
      { key: 'teams-list', label: '团队管理', path: '/teams', icon: React.createElement(TeamOutlined), roles: [...OPS_ROLES, ...ADMIN_ROLES] },
      { key: 'teams-members', label: '团队成员管理', path: '/teams/members', icon: React.createElement(TeamOutlined), roles: [...OPS_ROLES, ...ADMIN_ROLES] },
      { key: 'teams-orders', label: '团队订单', path: '/teams/orders', icon: React.createElement(FileTextOutlined), roles: [...OPS_ROLES, ...ADMIN_ROLES] },
    ],
  },
  {
    key: 'finance',
    label: '财务管理',
    icon: React.createElement(DollarOutlined),
    roles: [...OPS_ROLES, ...TECH_ROLES, ...FACTORY_ROLES, ...STORE_CUSTOMER_ROLES],
    children: [
      { key: 'finance-commission-calculator', label: '提成计算器', path: '/finance/commission-calculator', icon: React.createElement(CalculatorOutlined) },
      { key: 'finance-my-salary', label: '我的工资条', path: '/finance/my-salary', icon: React.createElement(FileTextOutlined) },
      { key: 'finance-salary-config', label: '薪酬参数设置', path: '/finance/salary-config', icon: React.createElement(SettingOutlined), roles: ADMIN_ROLES },
      { key: 'finance-salary', label: '工资汇总', path: '/finance/salary', icon: React.createElement(DollarOutlined), roles: FINANCE_ROLES },
      // 加工商对账已移至「供应商管理」分组第一项（2026-10-22），key 保持 finance-factory-reconciliation 不变（路由未变）
      { key: 'finance-year-end-bonus', label: '年终分红（完善中）', path: '/finance/year-end-bonus', icon: React.createElement(GiftOutlined), roles: FINANCE_ROLES },
      // 折扣审批 菜单暂时隐藏（路由保留）：finance-discount-approval /finance/discount-approval
    ],
  },
  {
    key: 'settings',
    label: '系统设置',
    icon: React.createElement(SettingOutlined),
    roles: ADMIN_ROLES,
    children: [
      { key: 'settings-user', label: '用户管理', path: '/settings/users', icon: React.createElement(UserOutlined), roles: ADMIN_ROLES },
      { key: 'settings-role', label: '角色权限', path: '/settings/roles', icon: React.createElement(SafetyOutlined), roles: ADMIN_ROLES },
      { key: 'settings-system-config', label: '系统配置', path: '/settings/system-config', icon: React.createElement(ToolOutlined), roles: ADMIN_ROLES },
      { key: 'settings-messages', label: '系统消息', path: '/settings/messages', icon: React.createElement(BellOutlined), roles: ADMIN_ROLES },
      { key: 'settings-operation-logs', label: '操作日志', path: '/settings/operation-logs', icon: React.createElement(FileTextOutlined), roles: ADMIN_ROLES },
      { key: 'settings-recycle-bin', label: '回收站', path: '/settings/recycle-bin', icon: React.createElement(DeleteOutlined), roles: ADMIN_ROLES },
    ],
  },
];

export function getMenuPaths(items: MenuItem[]): string[] {
  const paths: string[] = [];
  for (const item of items) {
    if (item.path) paths.push(item.path);
    if (item.children) paths.push(...getMenuPaths(item.children));
  }
  return paths;
}

export function findMenuByPath(items: MenuItem[], path: string): MenuItem | undefined {
  for (const item of items) {
    if (item.path === path) return item;
    if (item.children) {
      const found = findMenuByPath(item.children, path);
      if (found) return found;
    }
  }
  return undefined;
}
